import type { ComponentType } from "react";
import type { KeyboardShortcut } from "../keyboard-shortcuts/keyboard-shortcut";

/**
 * Generic action definition with metadata.
 * This is UI-agnostic; it contains only the metadata needed to describe an action.
 *
 * @template TContext - The type of entity this action operates on (e.g., Conversation, Order)
 * @template TActionId - The type of action IDs (usually a string literal union)
 */
export interface ActionDefinition<
  TContext = unknown,
  TActionId extends string = string,
> {
  /** Type-safe action identifier */
  id: TActionId;
  /** Human readable title for buttons, menus, command palette, etc. */
  title: string;
  /** Longer description explaining what the action does */
  description: string;
  /** Optional keyboard shortcut */
  shortcut?: KeyboardShortcut;
  /** Optional icon component */
  Icon?: ComponentType<{ className?: string }>;
  /** Optional category for grouping in UI (e.g., "Navigation", "Status") */
  category?: string;
  /**
   * Whether this action is enabled for a given context.
   * If not provided, defaults to true.
   */
  isEnabled?: (context: TContext) => boolean;
  /**
   * Whether this action should be visible for a given context.
   * If not provided, defaults to true.
   * Use this to hide actions that don't apply to certain context types.
   */
  isVisible?: (context: TContext) => boolean;
}

/**
 * An action definition with a bound execute function.
 * Created by handler hooks that bind actions to specific execution logic.
 *
 * @template TContext - The type of entity this action operates on
 * @template TActionId - The type of action IDs
 * @template TParams - Additional parameters required to execute the action (void if none)
 * @template TContextId - The type of context ID (e.g., branded string type)
 */
export interface BoundAction<
  TContext = unknown,
  TActionId extends string = string,
  TParams = void,
  TContextId = string,
> extends ActionDefinition<TContext, TActionId> {
  /**
   * Execute the action for a given context ID.
   * If TParams is void, only contextId is required.
   * Otherwise, both contextId and params are required.
   *
   * **Error handling:** When callers invoke execute with `void` (fire-and-forget),
   * this handler is responsible for handling its own errors (e.g., try/catch with
   * error toasts, logging). Callers using `void` will not catch any thrown errors.
   */
  execute: [TParams] extends [void]
    ? (contextId: TContextId) => void | Promise<void>
    : (contextId: TContextId, params: TParams) => void | Promise<void>;
}

/**
 * An action that is ready to execute.
 * This is an ActionDefinition with a bound execute function.
 * Used by command palette, menus, and other UI that needs to execute actions.
 */
export interface ExecutableAction<
  TContext = unknown,
  TActionId extends string = string,
> extends ActionDefinition<TContext, TActionId> {
  /**
   * Execute the action. Already bound to its context, so takes no parameters.
   *
   * **Error handling:** When callers invoke execute with `void` (fire-and-forget),
   * this handler is responsible for handling its own errors (e.g., try/catch with
   * error toasts, logging). Callers using `void` will not catch any thrown errors.
   */
  execute: () => void | Promise<void>;
}

/**
 * Type-safe action registry that maps action IDs to definitions.
 *
 * @template TActionId - String literal union of action IDs
 * @template TContext - The context type for visibility/enabled checks
 */
export type ActionRegistry<
  TActionId extends string,
  TContext = unknown,
> = Record<TActionId, ActionDefinition<TContext, TActionId>>;

/**
 * Type-safe map of action IDs to execute handlers.
 * Handlers are bound to a specific context, so they take no arguments.
 */
export type ActionHandlerMap<TActionId extends string = string> = Partial<
  Record<TActionId, () => void>
>;

/**
 * Get an action from a registry by ID.
 */
export function getAction<TActionId extends string, TContext = unknown>(
  registry: ActionRegistry<TActionId, TContext>,
  id: TActionId,
): ActionDefinition<TContext, TActionId> {
  return registry[id];
}

/**
 * Result of checking if an action can be executed.
 */
export interface ActionExecutionCheck {
  canExecute: boolean;
  reason?: string;
}

/**
 * Check if an action can be executed for a given context.
 */
export function checkActionExecution<TContext>(
  action: Pick<ActionDefinition<TContext>, "isVisible" | "isEnabled">,
  context: TContext,
): ActionExecutionCheck {
  if (action.isVisible && !action.isVisible(context)) {
    return {
      canExecute: false,
      reason: "Action not available for this context",
    };
  }
  if (action.isEnabled && !action.isEnabled(context)) {
    return { canExecute: false, reason: "Action is disabled for this context" };
  }
  return { canExecute: true };
}
